import { HttpContext } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { Observable, throwError } from 'rxjs'
import { catchError, map, tap } from 'rxjs/operators'
import {
  BatteryQuotePayload,
  BatteryQuotesResponse,
  BatteryQuote,
} from './quotes.types'
import { IndexedCollection } from '../../shared/types'
import { generateVehicleSlug } from '../../shared/utils/generateVehicleSlug'
import { TaggingService } from '../tagging/tagging.service'
import events from '../tagging/events'
import { AdobeEventTypes } from '../tagging/tagging.types'
import { AdobeEventService } from '../tagging/adobe/event-adobe.service'
import { BatteryQuotesResponseSchema } from './quotes.model'
import { DrrHttpClient } from '../../http/drr-http-client'
import { RETRY_CONFIG } from '../../shared/services/http-context.tokens'

@Injectable({
  providedIn: 'root',
})
export class QuotesService {
  constructor(
    private http: DrrHttpClient,
    private tagging: TaggingService,
    private adobeEventService: AdobeEventService,
  ) { }

  checkNotApplicableBatteryQuotes(quotes: BatteryQuote[]) {
    let failure = false;
    for (let index = 0; index < quotes.length; index++) {
      const element = quotes[index];
      if (!element?.memberPrice || element?.memberPrice === 'N/A') {
        failure = true
      }
    }

    return failure;
  }

  tagBatteryQuotes(quotePayload: BatteryQuotePayload, batteryQuotes: BatteryQuote[]) {
    const failedQuotes = this.checkNotApplicableBatteryQuotes(batteryQuotes)
    if (failedQuotes) {
      this.adobeEventService.sendEvent({
        eventName: AdobeEventTypes.SYSTEM,
        eventValue: events.dashboard.BATTERY_QUOTES_NO_PRICE
      })

      this.tagging.setAutomatedEvent(
        events.dashboard.BATTERY_QUOTES_NO_PRICE,
        events.dashboard.SERVICE_TRACKING_PAGE_TYPE,
        null,
        {
          vehicleYear: quotePayload.vehicle.year,
          vehicleMake: quotePayload.vehicle.make,
          vehicleModel: quotePayload.vehicle.model,
          zipCode: quotePayload.postalCode
        }
      )
    } else {
      this.adobeEventService.sendEvent({
        eventName: AdobeEventTypes.SYSTEM,
        eventValue: events.dashboard.BATTERY_QUOTES_SUCCESS
      })

      this.tagging.setAutomatedEvent(
        events.dashboard.BATTERY_QUOTES_SUCCESS,
        events.dashboard.SERVICE_TRACKING_PAGE_TYPE
      )
    }
  }

  getBatteryQuotes$(
    data: BatteryQuotePayload,
    club: string
  ): Observable<IndexedCollection<Array<BatteryQuote>>> {
    const context = new HttpContext()
      .set(RETRY_CONFIG, { ...RETRY_CONFIG.defaultValue(), enabled: false })

    return this.http
      .post<BatteryQuotesResponse>(
        `bff/club/${club}/battery-quotes`,
        data,
        { context }
      )
      .pipe(
        tap((response) => BatteryQuotesResponseSchema.validateSync(response)),
        map((response) => {
          const quotes = response?.batteryQuotes
          if (!quotes?.length) {
            throw new Error('No battery quote was found.')
          }

          const id = generateVehicleSlug(
            {
              year: data.vehicle.year,
              make: data.vehicle.make,
              model: data.vehicle.model,
            },
            data.postalCode
          )

          this.tagBatteryQuotes(data, quotes);

          return {
            [id]: quotes,
          }
        }),
        catchError((error) => {
          console.log('No battery quote was found.', data)
          // Propagate the original error: wrapping it in `new Error(error)`
          // stringified it and destroyed the normalized `.response` payload
          // the error reporting in quotes.effects.ts relies on.
          return throwError(error)
        })
      )
  }
}
